import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

/**
 * PFC-21 — the fiscal cabinet's electronic journal: a register's documents as
 * the tills uploaded them, in fiscal order, per-shift totals, numbering gaps,
 * and the CSV export. Money arrives as integer minor-unit strings.
 */

export type JournalRegister = {
  terminalId: string;
  terminalCode: string;
  terminalName: string;
  terminalKind: string;
  /** PFC-25: revoked tills are left out of the picker; a link to one still opens. */
  terminalStatus: 'provisioned' | 'active' | 'revoked';
  location: { id: string; code: string; name: string } | null;
  prro: {
    fiscalNumber: string | null;
    localNumber: string;
    prroName: string;
    registrationStatus: string;
  } | null;
};

export type JournalDocumentType = 'sale' | 'refund' | 'void' | 'copy' | 'x_report' | 'z_report';

export type JournalDocument = {
  entry: 'document';
  id: string;
  receiptType: JournalDocumentType;
  lifecycleState: string;
  finalisedAt: string;
  shiftId: string | null;
  hasFiscalBlock: boolean;
  receiptNumber: string | null;
  fiscalNumber: string | null;
  fiscalTimestamp: string | null;
  cashier: { id: string; displayName: string } | null;
  totalMinor: string;
  currency: string | null;
  /** `discountClassified` legs (points, a gift card sold as a discount) are not payments. */
  payments: { method: string; amountMinor: string; discountClassified: boolean }[] | null;
  tenderKind: string | null;
  originalReceiptId: string | null;
  isLegacy: boolean;
};

type Person = { id: string; displayName: string } | null;

/**
 * Back Office's own records of a register's drawer, listed among the
 * documents by time. The PRRO's fiscal text of these is not uploaded by the
 * tills yet (RL-540): they have no receipt number and never enter the
 * numbering check. Amounts are minor-unit strings; a cash movement's is a
 * magnitude and `direction` says which way it went.
 */
export type JournalCashMovement = {
  entry: 'cash_movement';
  id: string;
  movementId: string;
  shiftId: string;
  occurredAt: string;
  kind: string;
  direction: 'in' | 'out';
  amountMinor: string;
  reasonCode: string;
  notes: string | null;
  cashier: Person;
};

export type JournalShiftOpen = {
  entry: 'shift_open';
  id: string;
  shiftId: string;
  occurredAt: string;
  cashier: Person;
  openingCashMinor: string;
};

export type JournalShiftClose = {
  entry: 'shift_close';
  id: string;
  shiftId: string;
  occurredAt: string;
  cashier: Person;
  state: string;
  zReportRef: string | null;
  closingCashCountedMinor: string | null;
  closingCashExpectedMinor: string | null;
  varianceMinor: string | null;
  varianceReasonCode: string | null;
};

export type JournalBackOfficeRecord = JournalCashMovement | JournalShiftOpen | JournalShiftClose;

/** PFC-24 — the register's own documents that are not receipts, as the till uploaded them (RL-540). */
export type JournalFiscalDocumentType =
  | 'x_report'
  | 'z_report'
  | 'cash_in'
  | 'cash_out'
  | 'offline_session_open'
  | 'offline_session_close';

/** A Z report's totals, read off the register's own printout. Amounts are minor-unit strings. */
export type JournalZTotals = {
  source: 'register_text';
  currency: string | null;
  cashBalanceMinor: string | null;
  cashInMinor: string | null;
  cashOutMinor: string | null;
  byTaxGroup: {
    direction: 'sale' | 'refund';
    taxGroup: string;
    ratePercent: string | null;
    label: string;
    amountMinor: string;
  }[];
  byPayment: {
    direction: 'sale' | 'refund';
    method: 'cash' | 'card' | null;
    label: string;
    amountMinor: string;
  }[];
  lines: { section: string | null; label: string; amountMinor: string }[];
};

export type JournalFiscalDocument = {
  entry: 'fiscal_document';
  /** `fj:<documentId>` */
  id: string;
  documentId: string;
  documentType: JournalFiscalDocumentType;
  occurredAt: string;
  shiftId: string | null;
  registrarId: string | null;
  /** Issued by another register than this journal's (the till's PRRO was replaced). */
  otherRegister: boolean;
  shiftNumber: string | null;
  receiptNumber: string | null;
  fiscalNumber: string | null;
  fiscalTimestamp: string | null;
  cashierId: string | null;
  cashier: Person;
  online: boolean | null;
  offlineSession: { id: string | null; startedAt: string | null; documents: number | null } | null;
  testDocument: boolean;
  /** Cash in / out: the magnitude; the type says which way. */
  amountMinor: string | null;
  currency: string | null;
  hasText: boolean;
  zTotals: JournalZTotals | null;
  /** The Back Office record this document is the fiscal text of; it is not listed on its own. */
  backOffice: JournalCashMovement | JournalShiftClose | null;
};

export type JournalFiscalDocumentDetail = JournalFiscalDocument & {
  terminal: { id: string; code: string; displayName: string };
  fiscal: unknown;
  documentText: string | null;
  zTotalsRaw: unknown;
};

export type JournalEntry = JournalDocument | JournalBackOfficeRecord | JournalFiscalDocument;

export type JournalCashTotals = {
  cashInMinor: string;
  cashOutMinor: string;
  movementCount: number;
};

export type JournalDocumentDetail = JournalDocument & {
  subtotalMinor: string;
  totalDiscountMinor: string | null;
  terminal: { id: string; code: string; displayName: string };
  fiscal: unknown;
  receiptDocument: unknown;
};

export type JournalMoneyTotals = {
  currency: string | null;
  mixedCurrency: boolean;
  salesMinor: string | null;
  refundsMinor: string | null;
  netMinor: string | null;
  byPayment: {
    method: string | null;
    salesMinor: string;
    refundsMinor: string;
    netMinor: string;
  }[];
  documentCount: number;
  counts: Record<string, number>;
};

export type JournalGap = {
  after: string;
  before: string;
  missingFrom: string;
  missingTo: string;
  missingCount: string;
  shiftId: string | null;
  betweenShifts: boolean;
  /** Next to an offline session: the session's own documents, not missing ones. */
  nearOfflineSession: boolean;
};

export type JournalShift = {
  shiftId: string | null;
  openedAt: string | null;
  closedAt: string | null;
  state: string | null;
  cashier: { id: string; displayName: string } | null;
  zReportRef: string | null;
  firstNumber: string | null;
  lastNumber: string | null;
  totals: JournalMoneyTotals;
  /** The drawer: the float, the scope's cash in / out, and what the till recorded at close. */
  cash: JournalCashTotals & {
    openingCashMinor: string | null;
    closingCashExpectedMinor: string | null;
    closingCashCountedMinor: string | null;
    varianceMinor: string | null;
  };
  gaps: JournalGap[];
  /** The register's Z that closed the shift, when the till uploaded it. */
  zDocument: {
    id: string;
    documentId: string;
    shiftNumber: string | null;
    fiscalNumber: string | null;
  } | null;
};

export type JournalSummary = {
  register: JournalRegister;
  scope: { from: string | null; to: string | null; shiftId: string | null };
  totals: JournalMoneyTotals;
  cash: JournalCashTotals;
  shifts: JournalShift[];
  numbering: {
    numberedCount: number;
    firstNumber: string | null;
    lastNumber: string | null;
    /** Gaps not explained by an offline session. */
    gapCount: number;
    missingCount: string;
    offlineGapCount: number;
    offlineMissingCount: string;
    gaps: JournalGap[];
    gapsTruncated: boolean;
    duplicates: { receiptNumber: string; count: number }[];
    unnumberedCount: number;
  };
  withoutFiscalBlock: {
    count: number;
    items: { id: string; receiptType: JournalDocumentType; finalisedAt: string }[];
  };
  fiscalJournal: {
    /** The till has uploaded the register's journal (RL-540). */
    uploading: boolean;
    documentCount: number;
    counts: Record<JournalFiscalDocumentType, number>;
  };
  notUploaded: string[];
};

export type JournalPage = {
  register: JournalRegister;
  items: JournalEntry[];
  nextCursor: string | null;
};

/** Which register, and which slice of its journal. */
export type JournalScope = {
  terminalId: string;
  /** Instants, `from` inclusive and `to` exclusive (at most 31 days apart). */
  from: string;
  to: string;
  shiftId?: string;
};

export const JOURNAL_PAGE_SIZE = 100;

function scopeParams(scope: JournalScope): Record<string, string> {
  const params: Record<string, string> = {
    terminalId: scope.terminalId,
    from: scope.from,
    to: scope.to,
  };
  if (scope.shiftId !== undefined) params.shiftId = scope.shiftId;
  return params;
}

export function useJournalRegisters(enabled: boolean) {
  return useQuery({
    queryKey: ['fiscal-cabinet', 'journal', 'registers'],
    enabled,
    queryFn: async (): Promise<JournalRegister[]> =>
      (await apiClient.get<JournalRegister[]>('/fiscal-cabinet/journal/registers')).data,
  });
}

export function useJournalSummary(scope: JournalScope | null) {
  return useQuery({
    queryKey: ['fiscal-cabinet', 'journal', 'summary', scope],
    enabled: scope !== null,
    queryFn: async (): Promise<JournalSummary> =>
      (
        await apiClient.get<JournalSummary>('/fiscal-cabinet/journal/summary', {
          params: scopeParams(scope!),
        })
      ).data,
  });
}

export function useJournalDocuments(scope: JournalScope | null) {
  return useInfiniteQuery({
    queryKey: ['fiscal-cabinet', 'journal', 'documents', scope],
    enabled: scope !== null,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }): Promise<JournalPage> =>
      (
        await apiClient.get<JournalPage>('/fiscal-cabinet/journal', {
          params: {
            ...scopeParams(scope!),
            limit: JOURNAL_PAGE_SIZE,
            ...(pageParam === null ? {} : { cursor: pageParam }),
          },
        })
      ).data,
    getNextPageParam: (last) => last.nextCursor,
  });
}

export function useJournalDocument(id: string | null) {
  return useQuery({
    queryKey: ['fiscal-cabinet', 'journal', 'document', id],
    enabled: id !== null,
    queryFn: async (): Promise<JournalDocumentDetail> =>
      (await apiClient.get<JournalDocumentDetail>(`/fiscal-cabinet/journal/documents/${id}`)).data,
  });
}

export function useJournalFiscalDocument(documentId: string | null) {
  return useQuery({
    queryKey: ['fiscal-cabinet', 'journal', 'fiscal-document', documentId],
    enabled: documentId !== null,
    queryFn: async (): Promise<JournalFiscalDocumentDetail> =>
      (
        await apiClient.get<JournalFiscalDocumentDetail>(
          `/fiscal-cabinet/journal/fiscal-documents/${documentId}`,
        )
      ).data,
  });
}

/**
 * Download the scope as CSV. The server names the file; when the browser may
 * not read that header (a cross-origin dev setup), `fallbackName` is used.
 */
export async function downloadJournalCsv(
  scope: JournalScope,
  fallbackName: string,
): Promise<string> {
  const res = await apiClient.get<Blob>('/fiscal-cabinet/journal/export', {
    params: scopeParams(scope),
    responseType: 'blob',
    timeout: 120_000,
  });
  const disposition = String((res.headers as Record<string, unknown>)['content-disposition'] ?? '');
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName;
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  return filename;
}

export const JOURNAL_TYPE_LABEL: Record<JournalDocumentType, string> = {
  sale: 'Sale',
  refund: 'Refund',
  void: 'Void',
  copy: 'Copy',
  x_report: 'X-Report',
  z_report: 'Z-Report',
};

/** Reuses the receipts vocabulary: one word per document type across the admin UI. */
export const JOURNAL_TYPE_LABEL_KEY: Record<JournalDocumentType, string> = {
  sale: 'receipts.vocab.kindSale',
  refund: 'receipts.vocab.kindRefund',
  void: 'receipts.vocab.kindVoid',
  copy: 'receipts.vocab.kindCopy',
  x_report: 'receipts.vocab.kindXReport',
  z_report: 'receipts.vocab.kindZReport',
};

export const FISCAL_DOCUMENT_LABEL: Record<JournalFiscalDocumentType, string> = {
  x_report: 'X-Report',
  z_report: 'Z-Report',
  cash_in: 'Cash in',
  cash_out: 'Cash out',
  offline_session_open: 'Offline session started',
  offline_session_close: 'Offline session ended',
};

export const FISCAL_DOCUMENT_LABEL_KEY: Record<JournalFiscalDocumentType, string> = {
  x_report: 'receipts.vocab.kindXReport',
  z_report: 'receipts.vocab.kindZReport',
  cash_in: 'fiscalCabinet.journal.record.cashIn',
  cash_out: 'fiscalCabinet.journal.record.cashOut',
  offline_session_open: 'fiscalCabinet.journal.doc.offlineOpen',
  offline_session_close: 'fiscalCabinet.journal.doc.offlineClose',
};
